import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import { 
  FileText, PenLine, AlertTriangle, CheckCircle2, Clock, 
  Thermometer, XCircle, Plus, Upload, BarChart3, Eye,
  Calendar, Building2, RotateCcw, AlertCircle, TrendingUp,
  Database, Download, ShieldCheck, XCircle as XCircleIcon, Lock,
  UploadCloud, History, Trash2
} from 'lucide-react';

function StatCard({ icon: Icon, value, label, color, bg, onClick }) {
  return (
    <div className="stat-card" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <div className="stat-icon" style={{ background: bg }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-value">{value ?? '—'}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function EstadoBadge({ estado }) {
  const map = {
    firmado: { label: 'Firmado', cls: 'badge-firmado' },
    pendiente_firma: { label: 'Pdte. firma', cls: 'badge-pendiente_firma' },
    borrador: { label: 'Borrador', cls: 'badge-borrador' },
    rechazado: { label: 'Rechazado', cls: 'badge-rechazado' },
    archivado: { label: 'Archivado', cls: 'badge-archivado' },
  };
  const m = map[estado] || { label: estado, cls: '' };
  return <span className={`badge ${m.cls}`}>{m.label}</span>;
}


// ═══════════════════════════════════════════════════════════════
// MODAL: Descargar copia de seguridad
// ═══════════════════════════════════════════════════════════════
function ModalBackup({ onClose, onDescargado }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDescargar = async () => {
    if (!password) { setError('Debes introducir tu contraseña'); return; }
    setLoading(true); setError('');
    try {
      // Pedimos el backup como blob para descargarlo
      const response = await api.post('/backup/descargar', { password }, { responseType: 'blob' });
      
      // Extraer nombre del archivo del header
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `lomhifar-backup-${new Date().toISOString().slice(0,10)}.zip`;
      
      // Crear link de descarga
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      onDescargado(filename);
    } catch (err) {
      // Cuando la respuesta es blob pero hay error, hay que leerla como texto
      let msg = 'Error al descargar el backup';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          msg = json.error || msg;
        } catch {}
      } else if (err.response?.data?.error) {
        msg = err.response.data.error;
      }
      setError(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Database size={20} color="var(--color-primary)" /> Descargar Copia de Seguridad
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircleIcon size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="alert" style={{ background: '#dbeafe', color: '#1e40af', borderLeft: '4px solid #3b82f6' }}>
            <ShieldCheck size={16} /> 
            <div>
              <strong>Se descargará un archivo .zip con:</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: '0.85rem' }}>
                <li>Base de datos completa</li>
                <li>Todos los archivos adjuntos firmados</li>
                <li>Informe de contenido</li>
              </ul>
            </div>
          </div>
          
          <p style={{ marginTop: 12, fontSize: '0.9rem', color: 'var(--color-text-2)' }}>
            <strong>Guarda este archivo en un lugar seguro</strong>: pendrive, disco externo o nube. 
            Se recomienda hacer copia al menos una vez al mes.
          </p>
          
          <div className="form-group" style={{ marginTop: 16, padding: 12, background: '#fef3c7', borderRadius: 8, borderLeft: '4px solid #f59e0b' }}>
            <label className="form-label" style={{ color: '#92400e', fontWeight: 600 }}>
              <Lock size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
              Confirma con tu contraseña
            </label>
            <input type="password" className="form-control" value={password} 
              onChange={e => setPassword(e.target.value)} 
              placeholder="Tu contraseña actual"
              onKeyDown={e => e.key === 'Enter' && password && handleDescargar()} />
          </div>
          
          {error && <div className="alert alert-danger"><AlertCircle size={16} /> {error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={loading}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleDescargar} disabled={loading || !password}>
            {loading ? <><div className="spinner" style={{ width: 16, height: 16 }} /> Generando ZIP...</> : <><Download size={16} /> Descargar copia</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Restaurar copia de seguridad
// ═══════════════════════════════════════════════════════════════
function ModalRestaurar({ onClose, onRestaurado }) {
  const [archivo, setArchivo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mensajeOk, setMensajeOk] = useState('');
  const [preview, setPreview] = useState(null);
  const [analizando, setAnalizando] = useState(false);

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.zip')) {
      setError('El archivo debe ser un ZIP de backup');
      setArchivo(null);
      setPreview(null);
      return;
    }
    setError('');
    setArchivo(f);
    setPreview(null);

    // Analizar el ZIP ANTES de restaurar (avisar si es copia vieja)
    setAnalizando(true);
    try {
      const fd = new FormData();
      fd.append('archivo', f);
      const r = await api.post('/backup/previsualizar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      setPreview(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo analizar el ZIP');
      setArchivo(null);
    } finally {
      setAnalizando(false);
    }
  };

  const handleRestaurar = async () => {
    if (!archivo) { setError('Selecciona un archivo ZIP'); return; }
    if (!password) { setError('Debes introducir tu contraseña'); return; }
    if (confirmacion !== 'RESTAURAR') { setError('Debes escribir RESTAURAR exactamente'); return; }

    setLoading(true); setError('');

    try {
      const formData = new FormData();
      formData.append('archivo', archivo);
      formData.append('password', password);
      formData.append('confirmacion', confirmacion);

      const res = await api.post('/backup/restaurar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000  // 2 min por si el ZIP es grande
      });

      setMensajeOk(res.data.mensaje || 'Restauración iniciada. La app se reiniciará en unos segundos...');
      // Esperar 6 segundos y forzar logout + recarga (la app se está reiniciando)
      setTimeout(() => {
        localStorage.removeItem('token');
        window.location.href = '/';
      }, 6000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al restaurar el backup');
      setLoading(false);
    }
  };

  const formatBytes = (b) => {
    if (!b) return '—';
    if (b < 1024) return `${b} B`;
    if (b < 1024*1024) return `${(b/1024).toFixed(1)} KB`;
    return `${(b/(1024*1024)).toFixed(1)} MB`;
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UploadCloud size={20} color="#dc2626" /> Restaurar Copia de Seguridad
          </h2>
          {!loading && !mensajeOk && (
            <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircleIcon size={18} /></button>
          )}
        </div>
        <div className="modal-body">

          {mensajeOk ? (
            <div className="alert" style={{ background: '#dcfce7', color: '#166534', borderLeft: '4px solid #16a34a' }}>
              <CheckCircle2 size={16} />
              <div>
                <strong>{mensajeOk}</strong>
                <div style={{ marginTop: 6, fontSize: '0.85rem' }}>
                  Se te redirigirá al login en unos segundos. La aplicación se está reiniciando con los datos restaurados.
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="alert" style={{ background: '#fee2e2', color: '#991b1b', borderLeft: '4px solid #dc2626' }}>
                <AlertTriangle size={16} />
                <div>
                  <strong>ATENCIÓN: acción irreversible</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: '0.85rem' }}>
                    <li>Se SUSTITUIRÁN TODOS los datos actuales por los del ZIP</li>
                    <li>Todos los usuarios serán EXPULSADOS de su sesión</li>
                    <li>La app se reiniciará automáticamente</li>
                    <li>El sistema hará una copia automática previa por seguridad</li>
                  </ul>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: 16 }}>
                <label className="form-label">
                  <Upload size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                  Archivo de backup (.zip)
                </label>
                <input type="file" accept=".zip,application/zip" className="form-control"
                  onChange={handleFileChange} disabled={loading} />
                {archivo && (
                  <div style={{ marginTop: 6, fontSize: '0.85rem', color: '#16a34a' }}>
                    ✓ {archivo.name} ({formatBytes(archivo.size)})
                  </div>
                )}
                {analizando && (
                  <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#64748b' }}>
                    Analizando contenido de la copia...
                  </div>
                )}
                {preview && (
                  <div style={{
                    marginTop: 10, padding: '12px 14px', borderRadius: 8,
                    background: preview.posible_copia_vieja ? '#fef2f2' : '#ecfdf5',
                    border: `1px solid ${preview.posible_copia_vieja ? '#fca5a5' : '#6ee7b7'}`
                  }}>
                    <div style={{ fontSize: '0.85rem', color: preview.posible_copia_vieja ? '#991b1b' : '#065f46', lineHeight: 1.6 }}>
                      <strong>Contenido de esta copia:</strong><br />
                      📅 Fecha: {preview.fecha_copia}<br />
                      📎 Archivos en la copia: <strong>{preview.archivos_en_copia}</strong><br />
                      💾 Archivos en el sistema actual: <strong>{preview.archivos_actuales_bd}</strong>
                    </div>
                    <div style={{
                      marginTop: 10, padding: '8px 10px', borderRadius: 6,
                      background: preview.posible_copia_vieja ? '#fee2e2' : '#d1fae5',
                      fontSize: '0.82rem', fontWeight: 600,
                      color: preview.posible_copia_vieja ? '#b91c1c' : '#047857'
                    }}>
                      {preview.aviso}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group" style={{ padding: 12, background: '#fef3c7', borderRadius: 8, borderLeft: '4px solid #f59e0b' }}>
                <label className="form-label" style={{ color: '#92400e', fontWeight: 600 }}>
                  <Lock size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                  Tu contraseña
                </label>
                <input type="password" className="form-control" value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Tu contraseña actual" disabled={loading} />
              </div>

              <div className="form-group" style={{ padding: 12, background: '#fee2e2', borderRadius: 8, borderLeft: '4px solid #dc2626' }}>
                <label className="form-label" style={{ color: '#991b1b', fontWeight: 600 }}>
                  Confirmación: escribe <code style={{ background: '#fca5a5', padding: '2px 6px', borderRadius: 3 }}>RESTAURAR</code>
                </label>
                <input type="text" className="form-control" value={confirmacion}
                  onChange={e => setConfirmacion(e.target.value)}
                  placeholder="Escribe RESTAURAR en mayúsculas"
                  style={{ fontFamily: 'monospace', textTransform: 'uppercase' }}
                  disabled={loading} />
              </div>

              {error && <div className="alert alert-danger"><AlertCircle size={16} /> {error}</div>}
            </>
          )}
        </div>
        {!mensajeOk && (
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={onClose} disabled={loading}>Cancelar</button>
            <button className="btn" onClick={handleRestaurar}
              disabled={loading || !archivo || !password || confirmacion !== 'RESTAURAR'}
              style={{ background: '#dc2626', color: 'white', borderColor: '#dc2626' }}>
              {loading
                ? <><div className="spinner" style={{ width: 16, height: 16 }} /> Restaurando...</>
                : <><UploadCloud size={16} /> Restaurar ahora</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// TARJETA: Estado de la copia de seguridad
// ═══════════════════════════════════════════════════════════════
function TarjetaBackup({ isAdmin, onAbrirModal, onAbrirRestaurar }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get('/backup/info')
      .then(r => setInfo(r.data))
      .catch(() => setInfo({ ultimo_backup: null, total_backups: 0 }));
  }, []);

  // Solo para administradores (medida de seguridad)
  if (!isAdmin) return null;

  // Si aún no llegó la info, mostrar la tarjeta igualmente con datos vacíos
  // (nunca debe "desaparecer" por un fallo de red)
  const infoSegura = info || { ultimo_backup: null, total_backups: 0 };
  const ultimo = infoSegura.ultimo_backup;
  const diasDesde = ultimo ? Math.floor((Date.now() - new Date(ultimo.created_at).getTime()) / (1000 * 60 * 60 * 24)) : null;
  const necesitaBackup = !ultimo || diasDesde > 30;
  const formatBytes = (b) => {
    if (!b) return '—';
    if (b < 1024) return `${b} B`;
    if (b < 1024*1024) return `${(b/1024).toFixed(1)} KB`;
    if (b < 1024*1024*1024) return `${(b/(1024*1024)).toFixed(1)} MB`;
    return `${(b/(1024*1024*1024)).toFixed(2)} GB`;
  };

  return (
    <div className="card" style={{ 
      marginBottom: 24, 
      padding: '18px 22px',
      background: necesitaBackup ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
      border: necesitaBackup ? '1px solid #f59e0b' : '1px solid #3b82f6'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ 
            width: 48, height: 48, borderRadius: 12, 
            background: necesitaBackup ? '#f59e0b' : '#3b82f6',
            display: 'flex', alignItems: 'center', justifyContent: 'center' 
          }}>
            <Database size={24} color="white" />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', color: necesitaBackup ? '#92400e' : '#1e40af' }}>
              Copia de Seguridad {necesitaBackup && '⚠️'}
            </h3>
            <div style={{ fontSize: '0.85rem', color: necesitaBackup ? '#92400e' : '#1e40af', marginTop: 4 }}>
              {ultimo ? (
                <>
                  Última copia: <strong>{new Date(ultimo.created_at).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}</strong>
                  {' '}({diasDesde === 0 ? 'hoy' : diasDesde === 1 ? 'ayer' : `hace ${diasDesde} días`})
                  {' · '}
                  <span style={{ fontSize: '0.78rem' }}>{formatBytes(ultimo.tamano_bytes)} · {ultimo.num_archivos} archivo(s)</span>
                  {necesitaBackup && <div style={{ marginTop: 4, fontWeight: 600 }}>⚠ Llevas más de 30 días sin hacer copia. Se recomienda hacer una.</div>}
                </>
              ) : (
                <span style={{ fontWeight: 600 }}>⚠ Nunca se ha hecho una copia de seguridad. Se recomienda hacer una ahora.</span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn" onClick={onAbrirRestaurar}
            style={{ background: 'white', color: necesitaBackup ? '#92400e' : '#1e40af',
              borderColor: necesitaBackup ? '#f59e0b' : '#3b82f6',
              border: '1px solid' }}>
            <UploadCloud size={16} /> Restaurar
          </button>
          <button className="btn btn-primary" onClick={onAbrirModal}
            style={{ background: necesitaBackup ? '#f59e0b' : '#3b82f6', borderColor: necesitaBackup ? '#f59e0b' : '#3b82f6' }}>
            <Download size={16} /> Descargar copia
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal de limpieza de datos de prueba (doble confirmación)
function ModalLimpiarPrueba({ onClose, onLimpiado }) {
  const [paso, setPaso] = useState(1);
  const [texto, setTexto] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  const ejecutar = async () => {
    if (texto !== 'BORRAR') {
      setError('Debes escribir exactamente la palabra BORRAR (en mayúsculas).');
      return;
    }
    setCargando(true); setError('');
    try {
      const res = await api.post('/dashboard/limpiar-datos-prueba', { confirmacion: 'BORRAR' });
      onLimpiado(res.data?.mensaje || 'Datos de prueba eliminados correctamente.');
    } catch (e) {
      setError(e.response?.data?.error || 'Error al limpiar los datos de prueba.');
      setCargando(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16
    }}>
      <div className="card" style={{ maxWidth: 540, width: '100%', padding: 0, overflow: 'hidden' }}>
        <div style={{ background: '#dc2626', color: 'white', padding: '16px 22px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={22} />
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Limpiar datos de prueba</h3>
        </div>

        <div style={{ padding: '22px' }}>
          {paso === 1 && (
            <>
              <p style={{ marginTop: 0, fontWeight: 600, color: '#b91c1c' }}>
                ⚠ Esta acción es PERMANENTE y no se puede deshacer.
              </p>
              <p style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
                Se van a <strong>borrar todos los datos de prueba</strong>:
              </p>
              <ul style={{ fontSize: '0.86rem', lineHeight: 1.7, color: '#374151' }}>
                <li>Registros de Temperatura, Limpieza y Plagas</li>
                <li>Informes mensuales (incluso los firmados)</li>
                <li>Firmas y solicitudes de firma (historial)</li>
                <li>Devoluciones, No Conformidades, Proveedores, Formación</li>
              </ul>
              <div style={{
                background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 8,
                padding: '12px 14px', fontSize: '0.86rem', color: '#065f46', marginTop: 14
              }}>
                ✅ <strong>NO se tocan</strong> los 18 PNTs ni los usuarios/permisos.
                El sistema quedará limpio y listo para empezar en producción.
              </div>
              <div style={{ marginTop: 22, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
                <button className="btn" style={{ background: '#dc2626', color: 'white' }}
                  onClick={() => setPaso(2)}>
                  Entiendo, continuar
                </button>
              </div>
            </>
          )}

          {paso === 2 && (
            <>
              <p style={{ marginTop: 0, fontSize: '0.9rem', lineHeight: 1.6 }}>
                Para confirmar el borrado definitivo, escribe la palabra{' '}
                <strong style={{ color: '#dc2626' }}>BORRAR</strong> en el campo:
              </p>
              <input
                className="form-control"
                value={texto}
                onChange={e => { setTexto(e.target.value); setError(''); }}
                placeholder="Escribe BORRAR"
                autoFocus
                style={{ fontSize: '1rem', letterSpacing: 1 }}
              />
              {error && (
                <div style={{ color: '#dc2626', fontSize: '0.84rem', marginTop: 8 }}>{error}</div>
              )}
              <div style={{ marginTop: 22, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button className="btn btn-ghost" onClick={onClose} disabled={cargando}>Cancelar</button>
                <button className="btn" style={{ background: '#dc2626', color: 'white' }}
                  onClick={ejecutar} disabled={cargando || texto !== 'BORRAR'}>
                  {cargando ? 'Borrando...' : 'Borrar datos de prueba'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Tarjeta de limpieza de datos de prueba (solo admin)
function TarjetaLimpiarPrueba({ isAdmin, onAbrir }) {
  if (!isAdmin) return null;
  return (
    <div className="card" style={{
      marginBottom: 24, padding: '16px 22px',
      background: 'linear-gradient(135deg, #fff1f2 0%, #fecdd3 100%)',
      border: '1px solid #fb7185'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, background: '#e11d48',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Trash2 size={24} color="white" />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#9f1239' }}>
              Limpiar datos de prueba
            </h3>
            <div style={{ fontSize: '0.85rem', color: '#9f1239', marginTop: 4 }}>
              Borra registros, informes, firmas y datos de testeo.
              <strong> No toca los PNTs ni los usuarios.</strong> Usar antes de pasar a producción.
            </div>
          </div>
        </div>
        <button className="btn" onClick={onAbrir}
          style={{ background: '#e11d48', color: 'white', borderColor: '#e11d48' }}>
          <Trash2 size={16} /> Limpiar datos de prueba
        </button>
      </div>
    </div>
  );
}

// Modal de limpieza de archivos huérfanos del disco
function ModalHuerfanos({ onClose, onLimpiado }) {
  const [info, setInfo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [texto, setTexto] = useState('');
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/dashboard/archivos-huerfanos')
      .then(r => setInfo(r.data))
      .catch(e => setError(e.response?.data?.error || 'Error analizando archivos'))
      .finally(() => setCargando(false));
  }, []);

  const ejecutar = async () => {
    if (texto !== 'BORRAR') { setError('Escribe exactamente BORRAR (en mayúsculas).'); return; }
    setBorrando(true); setError('');
    try {
      const res = await api.post('/dashboard/limpiar-archivos-huerfanos', { confirmacion: 'BORRAR' });
      onLimpiado(res.data?.mensaje || 'Archivos huérfanos eliminados.');
    } catch (e) {
      setError(e.response?.data?.error || 'Error al limpiar.');
      setBorrando(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div className="card" style={{ maxWidth: 560, width: '100%', padding: 0, overflow: 'hidden' }}>
        <div style={{ background: '#b45309', color: 'white', padding: '16px 22px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={22} />
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Limpiar archivos huérfanos del disco</h3>
        </div>
        <div style={{ padding: 22 }}>
          {cargando && <p>Analizando archivos del servidor...</p>}

          {!cargando && info && (
            <>
              <p style={{ marginTop: 0, fontSize: '0.9rem', lineHeight: 1.6 }}>
                Archivos físicos en el servidor: <strong>{info.total_fisicos}</strong><br />
                Archivos que usan los PNTs: <strong>{info.total_registrados_bd}</strong><br />
                Archivos huérfanos (sobra, nadie los usa): <strong style={{ color: '#b45309' }}>{info.total_huerfanos}</strong>
              </p>
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '12px 14px', fontSize: '0.84rem', color: '#92400e', marginTop: 12 }}>
                {info.explicacion}
              </div>

              {info.total_huerfanos > 0 && (
                <>
                  <p style={{ fontSize: '0.88rem', marginTop: 18 }}>
                    Para borrar esos {info.total_huerfanos} archivos sobrantes, escribe <strong style={{ color: '#b45309' }}>BORRAR</strong>:
                  </p>
                  <input className="form-control" value={texto} autoFocus
                    onChange={e => { setTexto(e.target.value); setError(''); }}
                    placeholder="Escribe BORRAR" style={{ letterSpacing: 1 }} />
                </>
              )}
            </>
          )}

          {error && <div style={{ color: '#dc2626', fontSize: '0.84rem', marginTop: 10 }}>{error}</div>}

          <div style={{ marginTop: 22, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={borrando}>Cerrar</button>
            {!cargando && info && info.total_huerfanos > 0 && (
              <button className="btn" style={{ background: '#b45309', color: 'white' }}
                onClick={ejecutar} disabled={borrando || texto !== 'BORRAR'}>
                {borrando ? 'Borrando...' : `Borrar ${info.total_huerfanos} archivo(s)`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Tarjeta de archivos huérfanos (solo admin)
function TarjetaHuerfanos({ isAdmin, onAbrir }) {
  if (!isAdmin) return null;
  return (
    <div className="card" style={{
      marginBottom: 24, padding: '16px 22px',
      background: 'linear-gradient(135deg, #fffbeb 0%, #fde68a 100%)',
      border: '1px solid #f59e0b'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Trash2 size={24} color="white" />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#92400e' }}>
              Archivos huérfanos del disco
            </h3>
            <div style={{ fontSize: '0.85rem', color: '#92400e', marginTop: 4 }}>
              Limpia PDFs sobrantes de subidas/restauraciones anteriores que ningún PNT usa.
              <strong> No afecta a los PNTs actuales.</strong>
            </div>
          </div>
        </div>
        <button className="btn" onClick={onAbrir}
          style={{ background: '#d97706', color: 'white', borderColor: '#d97706' }}>
          <Trash2 size={16} /> Revisar y limpiar
        </button>
      </div>
    </div>
  );
}

// Tarjeta amarilla de recordatorios de informes pendientes (mes/trimestre anterior)
function TarjetaRecordatorios({ onIrInformes }) {
  const [rec, setRec] = useState(null);

  useEffect(() => {
    api.get('/dashboard/recordatorios-mensuales')
      .then(r => setRec(r.data))
      .catch(() => setRec(null));
  }, []);

  // Si no hay datos o no hay nada pendiente, no mostrar nada
  if (!rec || rec.todo_completado || !rec.pendientes || rec.pendientes.length === 0) {
    return null;
  }

  return (
    <div style={{
      background: '#FEF3C7',
      border: '1px solid #F59E0B',
      borderLeft: '5px solid #F59E0B',
      borderRadius: 10,
      padding: '18px 22px',
      marginBottom: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <AlertTriangle size={22} color="#B45309" />
        <span style={{ fontWeight: 700, fontSize: 16, color: '#92400E' }}>
          Informes pendientes
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rec.pendientes.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <span style={{ fontSize: 22, lineHeight: 1 }}>{p.icono}</span>
            <div>
              <div style={{ fontWeight: 600, color: '#78350F', fontSize: 14 }}>
                {p.nombre} — {p.periodo_nombre}
                <span style={{
                  marginLeft: 8,
                  fontSize: 11,
                  fontWeight: 500,
                  background: p.frecuencia === 'trimestral' ? '#DBEAFE' : '#FEF9C3',
                  color: p.frecuencia === 'trimestral' ? '#1E40AF' : '#854D0E',
                  padding: '2px 8px',
                  borderRadius: 10,
                }}>
                  {p.frecuencia === 'trimestral' ? 'Trimestral' : 'Mensual'}
                </span>
              </div>
              <div style={{ fontSize: 13, color: '#92400E', marginTop: 2 }}>
                {p.mensaje}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <button
          onClick={onIrInformes}
          style={{
            background: '#F59E0B',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '9px 18px',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Calendar size={16} /> Ir a Informes Mensuales
        </button>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalBackup, setModalBackup] = useState(false);
  const [modalRestaurar, setModalRestaurar] = useState(false);
  const [modalLimpiar, setModalLimpiar] = useState(false);
  const [modalHuerfanos, setModalHuerfanos] = useState(false);
  const [backupSuccess, setBackupSuccess] = useState('');
  const [reloadBackup, setReloadBackup] = useState(0);
  
  // Detectar admin
  const isAdmin = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.username === 'admin' || payload.rol === 'administrador';
    } catch { return false; }
  })();

  useEffect(() => {
    api.get('/dashboard').then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-overlay"><div className="spinner" /><span>Cargando panel...</span></div>;

  const { stats = {}, ultimos_pnts = [], firmas_pendientes = [], proximas_revisiones = [], alertas = [] } = data || {};

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1>Panel Principal</h1>
        <p style={{ marginTop: 4 }}>
          Bienvenido, <strong>{user?.nombre}</strong>. 
          Hoy es {new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}.
        </p>
      </div>

      {/* Mensaje de éxito de backup */}
      {backupSuccess && (
        <div className="alert alert-success" style={{ marginBottom: 16 }}>
          <CheckCircle2 size={16} /> {backupSuccess}
        </div>
      )}

      {/* Tarjeta amarilla de recordatorios de informes pendientes */}
      <TarjetaRecordatorios onIrInformes={() => navigate('/informes')} />

      {/* Tarjeta de Copia de Seguridad (solo admin) */}
      <TarjetaBackup key={reloadBackup} isAdmin={typeof isAdmin === 'function' ? isAdmin() : isAdmin}
        onAbrirModal={() => setModalBackup(true)}
        onAbrirRestaurar={() => setModalRestaurar(true)} />

      {/* Tarjeta de Limpiar datos de prueba (solo admin) */}
      <TarjetaLimpiarPrueba isAdmin={typeof isAdmin === 'function' ? isAdmin() : isAdmin}
        onAbrir={() => setModalLimpiar(true)} />

      <TarjetaHuerfanos isAdmin={typeof isAdmin === 'function' ? isAdmin() : isAdmin}
        onAbrir={() => setModalHuerfanos(true)} />

      {/* Alertas */}
      {alertas.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
          {alertas.map((a, i) => (
            <div key={i} className={`alert alert-${a.tipo}`}>
              <AlertCircle size={16} />
              {a.mensaje}
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid">
        <StatCard icon={FileText} value={stats.pnts_total} label="PNTs totales" color="#2196f3" bg="#dbeafe" onClick={() => navigate('/pnts')} />
        <StatCard icon={CheckCircle2} value={stats.pnts_firmados} label="PNTs firmados" color="#16a34a" bg="#dcfce7" onClick={() => navigate('/pnts?estado=firmado')} />
        <StatCard icon={Clock} value={stats.pnts_pendientes_firma} label="Pendientes firma" color="#d97706" bg="#fef3c7" onClick={() => navigate('/firmas')} />
        <StatCard icon={XCircle} value={stats.pnts_rechazados} label="Rechazados" color="#dc2626" bg="#fee2e2" onClick={() => navigate('/pnts?estado=rechazado')} />
        <StatCard icon={Building2} value={stats.proveedores_total} label="Proveedores" color="#9c27b0" bg="#f3e8ff" onClick={() => navigate('/proveedores')} />
        <StatCard icon={Thermometer} value={stats.temperaturas_hoy} label="Temp. hoy" color="#ef4444" bg="#fee2e2" onClick={() => navigate('/temperatura')} />
        <StatCard icon={AlertTriangle} value={stats.no_conformidades_abiertas} label="No conformidades" color="#f59e0b" bg="#fef3c7" onClick={() => navigate('/no-conformidades')} />
        <StatCard icon={RotateCcw} value={stats.devoluciones_pendientes} label="Devoluciones pdte." color="#6366f1" bg="#ede9fe" onClick={() => navigate('/devoluciones')} />
      </div>

      {/* Accesos rápidos */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ marginBottom: 14 }}>Acceso Rápido</h2>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => navigate('/pnts')}>
            <Plus size={16} /> Nuevo PNT
          </button>
          <button className="btn btn-dark" onClick={() => navigate('/temperatura')}>
            <Thermometer size={16} /> Registrar Temperatura
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/firmas')}>
            <PenLine size={16} /> Ver Firmas Pendientes
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/archivos')}>
            <Upload size={16} /> Subir Documento
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/informes')}>
            <BarChart3 size={16} /> Generar Informe
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
        {/* Últimos PNTs */}
        <div className="card" style={{ gridColumn: '1 / 3' }}>
          <div className="card-header">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileText size={16} color="var(--color-primary)" />
              Últimos Documentos
            </h3>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/pnts')}>
              <Eye size={14} /> Ver todos
            </button>
          </div>
          {ultimos_pnts.length === 0 ? (
            <div className="empty-state" style={{ padding: 30 }}>
              <FileText size={32} />
              <p>No hay documentos</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Título</th>
                    <th>Categoría</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {ultimos_pnts.map(p => (
                    <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/pnts/${p.id}`)}>
                      <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--color-primary)', fontWeight: 600 }}>{p.codigo}</span></td>
                      <td style={{ fontWeight: 500 }}>{p.titulo}</td>
                      <td>
                        <span style={{ fontSize: '0.75rem', color: p.color, fontWeight: 600 }}>
                          {p.categoria}
                        </span>
                      </td>
                      <td><EstadoBadge estado={p.estado} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Firmas pendientes y revisiones */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="card">
            <div className="card-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <PenLine size={16} color="var(--color-primary)" />
                Firmas Pendientes
              </h3>
            </div>
            {firmas_pendientes.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-3)', fontSize: '0.875rem' }}>
                ✓ No hay firmas pendientes
              </div>
            ) : (
              <div style={{ padding: '0 0 4px' }}>
                {firmas_pendientes.slice(0, 4).map(f => (
                  <div key={f.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--color-gray-2)', cursor: 'pointer' }} onClick={() => navigate('/firmas')}>
                    <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{f.pnt_titulo}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>
                      {f.solicitante} · {new Date(f.created_at).toLocaleDateString('es-ES')}
                    </div>
                  </div>
                ))}
                {firmas_pendientes.length > 4 && (
                  <div style={{ padding: 10, textAlign: 'center' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate('/firmas')}>
                      Ver {firmas_pendientes.length - 4} más
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Calendar size={16} color="var(--color-primary)" />
                Próximas Revisiones
              </h3>
            </div>
            {proximas_revisiones.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-3)', fontSize: '0.875rem' }}>
                Sin revisiones próximas
              </div>
            ) : (
              <div style={{ padding: '0 0 4px' }}>
                {proximas_revisiones.map(p => (
                  <div key={p.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--color-gray-2)', cursor: 'pointer' }} onClick={() => navigate(`/pnts/${p.id}`)}>
                    <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{p.titulo}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-warning)', fontWeight: 500 }}>
                      Revisión: {new Date(p.fecha_proxima_revision).toLocaleDateString('es-ES')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {modalBackup && <ModalBackup 
        onClose={() => setModalBackup(false)} 
        onDescargado={(filename) => {
          setModalBackup(false);
          setBackupSuccess(`✅ Copia descargada: ${filename}. Guárdala en lugar seguro.`);
          setReloadBackup(r => r + 1);
          setTimeout(() => setBackupSuccess(''), 8000);
        }} 
      />}

      {modalRestaurar && <ModalRestaurar
        onClose={() => setModalRestaurar(false)}
        onRestaurado={() => {}}
      />}

      {modalLimpiar && <ModalLimpiarPrueba
        onClose={() => setModalLimpiar(false)}
        onLimpiado={(msg) => {
          setModalLimpiar(false);
          setBackupSuccess('✅ ' + msg);
          setReloadBackup(r => r + 1);
          api.get('/dashboard').then(r => setData(r.data));
          setTimeout(() => setBackupSuccess(''), 8000);
        }}
      />}

      {modalHuerfanos && <ModalHuerfanos
        onClose={() => setModalHuerfanos(false)}
        onLimpiado={(msg) => {
          setModalHuerfanos(false);
          setBackupSuccess('✅ ' + msg);
          setTimeout(() => setBackupSuccess(''), 8000);
        }}
      />}
    </div>
  );
}
